import { useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import { CardComp, TableCompData } from "../../../../components";
import TableSkeletonLoader from "../../../../components/Common/TableSkeletonLoader";
import ConfirmationModal from "../../../../components/Common/ConfirmationModal";
import AdminCategoryModal from "../../../../components/Admin/InvestorPortal/AdminCategoryModal";
import InvestorPortalPageHeader, {
  InvestorPortalPrimaryButton,
} from "../../../../components/Admin/InvestorPortal/InvestorPortalPageHeader";
import InvestorPortalTableEmpty from "../../../../components/Admin/InvestorPortal/InvestorPortalTableEmpty";
import InvestorPortalTableSection from "../../../../components/Admin/InvestorPortal/InvestorPortalTableSection";
import TableActionDropdown from "../../../../components/Admin/InvestorPortal/TableActionDropdown";
import {
  useListStaffMarketplaceCategoriesQuery,
  useCreateStaffMarketplaceCategoryMutation,
  useUpdateStaffMarketplaceCategoryMutation,
  useActivateStaffMarketplaceCategoryMutation,
  useDeactivateStaffMarketplaceCategoryMutation,
} from "../../../../store/admin/marketplace/staffMarketplaceApi";
import { withMarketplaceGuard } from "../withInvestorPortalGuard";

const PER_PAGE = 20;

function CategoriesPage() {
  const [page, setPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const { data, isFetching, isError, refetch } = useListStaffMarketplaceCategoriesQuery({
    page,
    per_page: PER_PAGE,
  });
  const [createCategory] = useCreateStaffMarketplaceCategoryMutation();
  const [updateCategory] = useUpdateStaffMarketplaceCategoryMutation();
  const [activateCategory] = useActivateStaffMarketplaceCategoryMutation();
  const [deactivateCategory] = useDeactivateStaffMarketplaceCategoryMutation();

  const items = data?.items || [];
  const apiPagination = data?.pagination || {};
  const pagination = {
    currentPage: apiPagination.current_page || page,
    totalPages: apiPagination.last_page || 1,
    total: apiPagination.total || items.length,
    perPage: apiPagination.per_page || PER_PAGE,
    onPageChange: setPage,
  };

  const parentOptions = useMemo(
    () => items.filter((c) => !c.parentId),
    [items]
  );

  /** Categories first, then their subcategories — easier to scan. */
  const sortedItems = useMemo(() => {
    const categories = items.filter((c) => !c.parentId);
    const subcategories = items.filter((c) => c.parentId);
    const ordered = [];
    categories.forEach((cat) => {
      ordered.push(cat);
      subcategories
        .filter((s) => String(s.parentId) === String(cat.id))
        .forEach((s) => ordered.push(s));
    });
    // Orphaned subcategories (parent not on this page)
    subcategories
      .filter((s) => !ordered.some((o) => o.id === s.id))
      .forEach((s) => ordered.push(s));
    return ordered;
  }, [items]);

  const openCreate = () => {
    setEditing(null);
    setModalOpen(true);
  };

  const openEdit = (category) => {
    setEditing(category);
    setModalOpen(true);
  };

  const handleSubmit = async (values) => {
    const body = {
      name: values.name,
      parent_id: values.parentId || null,
      sort_order: values.sortOrder,
      description: values.description || undefined,
    };

    const isSub = Boolean(body.parent_id);
    try {
      if (editing?.id) {
        await updateCategory({ id: editing.id, body }).unwrap();
        if (values.isActive && !editing.isActive) {
          await activateCategory(editing.id).unwrap();
        } else if (!values.isActive && editing.isActive) {
          await deactivateCategory(editing.id).unwrap();
        }
        toast.success(isSub ? "Subcategory updated" : "Category updated");
      } else {
        const created = await createCategory(body).unwrap();
        if (values.isActive === false && created?.id) {
          await deactivateCategory(created.id).unwrap();
        }
        toast.success(isSub ? "Subcategory created" : "Category created");
      }
    } catch (error) {
      toast.error(error?.data?.message || "Unable to save.");
    }
  };

  const toggleActive = (category) => {
    const isSub = Boolean(category.parentId);
    const label = isSub ? "subcategory" : "category";
    const nextActive = !category.isActive;
    setConfirm({
      title: nextActive ? `Activate ${label}` : `Deactivate ${label}`,
      message: `${nextActive ? "Activate" : "Deactivate"} "${category.name}"?`,
      confirmText: nextActive ? "Activate" : "Deactivate",
      confirmButtonClass: nextActive ? "bg-primary1 text-white" : "bg-danger text-white",
      action: async () => {
        try {
          if (nextActive) {
            await activateCategory(category.id).unwrap();
            toast.success(isSub ? "Subcategory activated" : "Category activated");
          } else {
            await deactivateCategory(category.id).unwrap();
            toast.success(isSub ? "Subcategory deactivated" : "Category deactivated");
          }
        } catch (error) {
          toast.error(error?.data?.message || "Unable to update status.");
        }
        setConfirm(null);
      },
    });
  };

  const columns = [
    {
      name: "Name",
      cell: (row) => (
        <span className={row.parentId ? "ps-3" : "fw-semibold"}>{row.name}</span>
      ),
      minWidth: "180px",
      grow: 2,
    },
    {
      name: "Type",
      cell: (row) => (
        <span
          className="badge rounded-pill px-3 py-2"
          style={{
            backgroundColor: row.parentId ? "#31778c" : "#8b313a",
            color: "#fff",
            minWidth: 100,
          }}
        >
          {row.parentId ? "Subcategory" : "Category"}
        </span>
      ),
      minWidth: "130px",
    },
    {
      name: "Category",
      selector: (row) => (row.parentId ? row.parentName || "—" : "—"),
      minWidth: "140px",
    },
    { name: "Sort", selector: (row) => row.sortOrder ?? 0, minWidth: "80px", center: true },
    { name: "Products", selector: (row) => row.productCount ?? 0, minWidth: "90px", center: true },
    {
      name: "Status",
      cell: (row) => (
        <span
          className="badge rounded-pill px-3 py-2"
          style={{
            backgroundColor: row.isActive ? "#19BC31" : "#A1A0A0",
            color: "#fff",
            minWidth: 90,
          }}
        >
          {row.isActive ? "Active" : "Inactive"}
        </span>
      ),
      minWidth: "110px",
    },
    {
      name: "Actions",
      cell: (row) => (
        <TableActionDropdown
          items={[
            { name: "Edit", action: () => openEdit(row) },
            {
              name: row.isActive ? "Deactivate" : "Activate",
              action: () => toggleActive(row),
            },
          ]}
          row={row}
        />
      ),
      width: "80px",
      center: true,
    },
  ];

  const emptyState = isError ? (
    <InvestorPortalTableEmpty
      variant="error"
      title="We couldn’t load categories."
      onCta={() => refetch()}
    />
  ) : (
    <InvestorPortalTableEmpty
      variant="empty"
      title="No categories yet."
      description="Create a category, then add subcategories to organize products."
      ctaLabel="New category"
      onCta={openCreate}
    />
  );

  return (
    <div className="pb-4">
      <InvestorPortalPageHeader
        title="Marketplace Categories"
        subtitle="Organize products with categories and subcategories."
        action={<InvestorPortalPrimaryButton btnText="New category" onClick={openCreate} />}
      />

      <InvestorPortalTableSection pagination={pagination}>
        <TableCompData
          columns={columns}
          data={sortedItems}
          progressPending={isFetching}
          progressComponent={<TableSkeletonLoader rows={8} columns={7} />}
          noDataComponent={emptyState}
        />
      </InvestorPortalTableSection>

      <AdminCategoryModal
        show={modalOpen}
        category={editing}
        parentOptions={parentOptions.filter((p) => p.id !== editing?.id)}
        onClose={() => {
          setModalOpen(false);
          setEditing(null);
        }}
        onSubmit={handleSubmit}
      />

      <ConfirmationModal
        isOpen={Boolean(confirm)}
        onClose={() => setConfirm(null)}
        onConfirm={confirm?.action}
        title={confirm?.title}
        message={confirm?.message}
        confirmText={confirm?.confirmText}
        confirmButtonClass={confirm?.confirmButtonClass}
      />
    </div>
  );
}

export default withMarketplaceGuard(CategoriesPage);
